import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ImageWithSkeleton } from "../components/ImageWithSkeleton";

const theProductImg = "/images/the-product.jpg";
const ourVisionImg = "/images/our-vision.jpg";
const ourMissionImg = "/images/our-mission.jpg";
const award1Img = "/images/award-1.jpg";
const award11Img = "/images/award-11.jpg";
const newspaper1Img = "/images/newspaper-1.jpg";
const newspaper2Img = "/images/newspaper-2.jpg";
const interview1Img = "/images/Interview-1.jpg";
const interview2Img = "/images/interview-2.jpg";
const interview3Img = "/images/interview-3.jpg";

const VideoCard = ({
  thumbnail,
  videoId,
  caption,
}: {
  thumbnail: string;
  videoId: string;
  caption: string;
}) => {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="group">
      <div
        className="aspect-video w-full overflow-hidden rounded-none border-2 border-white/10 hover:border-[var(--color-sansi-green)] relative bg-black/50 cursor-pointer transition-all duration-300 transform hover:-translate-y-2 hover:shadow-[8px_8px_0px_var(--color-sansi-orange)]"
        onClick={() => setIsPlaying(true)}
      >
        {!isPlaying ? (
          <>
            <ImageWithSkeleton
              src={thumbnail}
              alt={caption}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
              <div className="w-16 h-16 rounded-full bg-[var(--color-sansi-orange)] flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg">
                <svg
                  className="w-8 h-8 text-black fill-current translate-x-0.5"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </>
        ) : (
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
            title="Interview Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        )}
      </div>
      <p className="mt-6 text-lg text-gray-300 font-light leading-relaxed">
        {caption}
      </p>
    </div>
  );
};

export function OurStory() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen pt-32 pb-24 px-6 container mx-auto flex flex-col items-center">
      {/* Header */}
      <div className="text-center space-y-6 mb-24 max-w-4xl">
        <h1 className="font-display text-4xl md:text-7xl uppercase text-white leading-none">
          Rooted in <br />
          <span className="text-[var(--color-sansi-green)]">Sri Lanka</span>
        </h1>
        <p className="text-xl md:text-2xl text-gray-400 font-light">
          From the lush tropical gardens to your hands, our journey is one of
          passion, purity, and preservation.
        </p>
      </div>

      {/* The Product Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center mb-32 w-full">
        <div className="order-2 md:order-1 space-y-8">
          <h2 className="font-display text-4xl md:text-5xl uppercase text-white">
            The{" "}
            <span className="text-[var(--color-sansi-orange)]">Product</span>
          </h2>
          <div className="space-y-6 text-lg text-gray-300 font-light leading-relaxed">
            <p>
              Sansi Eco Foods is a sustainable food brand specializing in
              high-quality dehydrated fruits and vegetables. We carefully select
              fresh, locally sourced produce and use advanced dehydration
              techniques to preserve natural flavor, color, and nutritional
              value—without artificial preservatives or additives.
            </p>
            <p>
              Our dehydration process extends shelf life while maintaining the
              goodness of farm-fresh ingredients, making our products
              convenient, healthy, and perfect for modern lifestyles. From
              everyday cooking to healthy snacking, Sansi Eco Foods delivers
              natural solutions you can trust.
            </p>
            <p>
              Driven by eco-friendly practices, we support local farmers and
              promote responsible food processing that reduces waste and
              protects the environment. At Sansi Eco Foods, we turn fresh
              harvests into long-lasting, nutritious foods—naturally.
            </p>
            <ul className="space-y-4 pt-4">
              <li className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-[var(--color-sansi-green)]/20 flex items-center justify-center text-[var(--color-sansi-green)] font-bold">
                  01
                </span>
                <span>100% Natural Fruits</span>
              </li>
              <li className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-[var(--color-sansi-green)]/20 flex items-center justify-center text-[var(--color-sansi-green)] font-bold">
                  02
                </span>
                <span>No Added Sugar or Preservatives</span>
              </li>
              <li className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-full bg-[var(--color-sansi-green)]/20 flex items-center justify-center text-[var(--color-sansi-green)] font-bold">
                  03
                </span>
                <span>Nutrient Dense & Flavorful</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="order-1 md:order-2 relative">
          <div className="aspect-square rounded-full overflow-hidden border-4 border-white/10 relative z-10">
            <ImageWithSkeleton
              src={theProductImg}
              alt="Mixed Dried Fruits"
              className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
              rounded={true}
            />
          </div>
          <div className="absolute top-0 right-0 w-full h-full bg-[var(--color-sansi-orange)]/20 rounded-full blur-[100px] -z-10" />
        </div>
      </div>

      {/* The Vision Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center mb-32 w-full">
        <div className="relative">
          <div className="aspect-square rounded-full overflow-hidden border-4 border-white/10 relative z-10">
            <ImageWithSkeleton
              src={ourVisionImg}
              alt="Our Vision"
              className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
              rounded={true}
            />
          </div>
          <div className="absolute bottom-0 left-0 w-full h-full bg-[var(--color-sansi-orange)]/20 rounded-full blur-[100px] -z-10" />
        </div>

        <div className="space-y-8">
          <h2 className="font-display text-4xl md:text-5xl uppercase text-white">
            Our <span className="text-[var(--color-sansi-orange)]">Vision</span>
          </h2>
          <div className="space-y-6 text-lg text-gray-300 font-light leading-relaxed">
            <p>
              To become Sri Lanka’s most trusted brand for natural,
              chemical-free dehydrated snacks and a leader in sustainable
              value-added agriculture in the Putalam District.
            </p>
          </div>
        </div>
      </div>

      {/* The Mission Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center w-full">
        <div className="order-2 md:order-1 space-y-8">
          <h2 className="font-display text-4xl md:text-5xl uppercase text-white">
            Our <span className="text-[var(--color-sansi-green)]">Mission</span>
          </h2>
          <div className="space-y-6 text-lg text-gray-300 font-light leading-relaxed">
            <p>
              To produce premium-quality, healthy, preservative-free dehydrated
              fruit and vegetable snacks using locally sourced raw materials,
              while supporting rural farmers and promoting healthier eating
              habits.
            </p>
            <div className="pt-6">
              <button onClick={() => navigate("/")} className="px-8 py-4 bg-[var(--color-sansi-green)] text-black font-black uppercase tracking-wider hover:bg-white transition-colors duration-300 transform hover:-translate-y-1 hover:shadow-[4px_4px_0px_var(--color-sansi-orange)]">
                Join Our Journey
              </button>
            </div>
          </div>
        </div>

        <div className="order-1 md:order-2 relative">
          <div className="aspect-square rounded-full overflow-hidden border-4 border-white/10 relative z-10">
            <ImageWithSkeleton
              src={ourMissionImg}
              alt="Our Mission"
              className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
              rounded={true}
            />
          </div>
          <div className="absolute bottom-0 left-0 w-full h-full bg-[var(--color-sansi-green)]/20 rounded-full blur-[100px] -z-10" />
        </div>
      </div>

      {/* Interviews Section */}
      <div className="w-full mt-32">
        <h2 className="font-display text-4xl md:text-5xl uppercase text-white text-center mb-16">
          <span className="text-[var(--color-sansi-orange)]">Media</span> &
          Interviews
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24">
          <VideoCard
            thumbnail={interview1Img}
            videoId="tNZPSmUfaN4"
            caption="Every conversation is a step forward — thankful for the interview opportunity with Thai Sri Academy on Batapola TV."
          />
          <VideoCard
            thumbnail={interview2Img}
            videoId="qcKqwCoP4yo"
            caption="Successfully featured on the Inside Story with Hasini YouTube channel, sharing insights, experiences, and real-life perspectives."
          />
          <VideoCard
            thumbnail={interview3Img}
            videoId="0P0Jk8L87H4"
            caption="Grateful for the opportunity to share my thoughts on TV Derana's &quot;To the Point&quot; with Wasantha Nanayakkara, on one of Sri Lanka's most popular television channels"
          />
        </div>
      </div>

      {/* Awards and Highlights Section */}
      <div className="w-full mt-32">
        <h2 className="font-display text-4xl md:text-5xl uppercase text-white text-center mb-16">
          Awards and{" "}
          <span className="text-[var(--color-sansi-green)]">Highlights</span>
        </h2>

        {/* Award Section */}
        <div className="mb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-8">
            <div className="aspect-[4/3] w-full overflow-hidden rounded-none border-2 border-white/10 hover:border-[var(--color-sansi-green)] relative bg-black/50 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-[8px_8px_0px_var(--color-sansi-orange)] cursor-pointer">
              <ImageWithSkeleton
                src={award1Img}
                alt="Best Young Entrepreneur Award"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="aspect-[4/3] w-full overflow-hidden rounded-none border-2 border-white/10 hover:border-[var(--color-sansi-green)] relative bg-black/50 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-[8px_8px_0px_var(--color-sansi-orange)] cursor-pointer">
              <ImageWithSkeleton
                src={award11Img}
                alt="Best Young Entrepreneur Award"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
          <p className="text-lg text-gray-300 font-light leading-relaxed text-center max-w-4xl mx-auto">
            Honored to receive the award as the Best Young Entrepreneur using
            modern technology for business in the Puttalam District,
            Nawagattegama Divisional Area — truly motivated and inspired to keep
            growing and innovating.
          </p>
        </div>

        {/* Newspaper Section */}
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mb-8">
            <div className="aspect-[4/3] w-full overflow-hidden rounded-none border-2 border-white/10 hover:border-[var(--color-sansi-green)] relative bg-black/50 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-[8px_8px_0px_var(--color-sansi-orange)] cursor-pointer">
              <ImageWithSkeleton
                src={newspaper1Img}
                alt="Sansi Eco Foods in Navaliya newspaper"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="aspect-[4/3] w-full overflow-hidden rounded-none border-2 border-white/10 hover:border-[var(--color-sansi-green)] relative bg-black/50 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-[8px_8px_0px_var(--color-sansi-orange)] cursor-pointer">
              <ImageWithSkeleton
                src={newspaper2Img}
                alt="Sansi Eco Foods in Dinamina newspaper"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
          <p className="text-lg text-gray-300 font-light leading-relaxed text-center max-w-4xl mx-auto">
            Proud to see the growth journey of Sansi Eco Foods featured in two
            leading newspapers — Navaliya and Dinamina.
          </p>
        </div>
      </div>
    </div>
  );
}
